import {
  annualCentsFor,
  type CountryCode,
  EMAIL_PROBLEM,
  formatMoney,
  furthestStart,
  isEmailAddress,
  rateForCadence,
} from '@app/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { ReactNode, Ref } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { DEFAULT_COUNTRY, type Place, postalLabel } from '../lib/address-place'
import { readCivilDate } from '../lib/today'
import type { RouterOutputs } from '../lib/trpc'
import { useTRPC } from '../lib/trpc'
import { continueLabel } from '../lib/two-step-payment'
import {
  AddressPlace,
  AliyaNameReading,
  AnswerLine,
  Ask,
  BranchList,
  BranchRow,
  Button,
  Choice,
  type ChoiceOption,
  DateInput,
  Field,
  FieldRow,
  FieldSection,
  Notice,
  PaymentStep,
  Question,
  Select,
  TextInput,
  TotalLine,
  useCheckoutConfirmation,
} from '../ui'
import { Challenge } from '../ui/Challenge'
import { AccountLinkRequest } from './AccountLinkRequest'
import { forgetAnswers } from './donate-answers'
import {
  type MembershipAnswers,
  rememberMembership,
} from './membership-answers'

const money = formatMoney

type Tier = RouterOutputs['donation']['tiers']['tiers'][number]
type Reading = RouterOutputs['member']['parseAliyaName']

/** A person the joiner names, as the form holds them before anything is sent. */
type Person = {
  /** Stable while the row is on screen, so React keeps the field it is typing in. */
  id: string
  role: 'head' | 'child'
  firstName: string
  lastName: string
  aliyaTyped: string
  yichus: 'kohen' | 'levi' | 'yisrael' | ''
}

const YICHUS: ChoiceOption<'kohen' | 'levi' | 'yisrael'>[] = [
  { value: 'kohen', label: 'כהן' },
  { value: 'levi', label: 'לוי' },
  { value: 'yisrael', label: 'ישראל' },
]

/**
 * Joining the shul, or reaching an existing membership — one question at a
 * time, the same as every other branch of this page.
 *
 * **The two sides are separate paths.** A standing member enters an email and
 * is mailed their account link, where the membership is renewed, changed or
 * ended — the same request View my account makes. Joining gathers the
 * household and then the names the gabbai calls up, each one read back as the
 * server understood it — a wrong split is a wrong name called out in shul, and
 * the person standing there is the only one who can catch it.
 *
 * Yahrzeits are not asked for. They are a date most people cannot convert
 * standing in a doorway, and the office collects them afterwards.
 */
export function MembershipBranch({
  orgId,
  tiers,
  today,
  stepRef,
  held,
  onCheckoutComplete,
}: {
  orgId: string
  tiers: Tier[]
  /**
   * The shul's own day, as the server reads it.
   *
   * A public join stores a card and charges nothing; the first payment is
   * raised when the period begins. A browser in another zone is having a
   * different day, so the start date is handed down rather than derived twice.
   */
  today: string
  /**
   * Where focus goes when a question comes into hand.
   *
   * Optional because the page leaves it off while this branch sits hidden
   * behind its own reopened question: a hidden region cannot take focus, and
   * the ref belongs to the question the page is asking instead.
   */
  stepRef?: Ref<HTMLDivElement>
  /**
   * Whether the page is holding this branch hidden while it asks its own
   * question again.
   *
   * Every answer here is kept — picking membership a second time finds them
   * intact — but no checkout is: one opened from these answers, or
   * still being opened, is discarded, so a request resolving late cannot draw
   * the card step over the question the page is asking.
   */
  held: boolean
  onCheckoutComplete: (checkoutId: string) => void
}) {
  const trpc = useTRPC()
  const challenge = useQuery(trpc.turnstile.siteKey.queryOptions())

  const [tierId, setTierId] = useState<string | null>(null)
  const [pendingTier, setPendingTier] = useState<string>('')
  const [cadence, setCadence] = useState<'monthly' | 'annual' | null>(null)
  const [pendingCadence, setPendingCadence] = useState<string>('')
  const [joining, setJoining] = useState<boolean | null>(null)
  /* A question reopened from its folded line. Reopening asks it again and
     clears nothing: what was answered below it stays held, and only the new
     answer decides which of those still apply. */
  const [reopened, setReopened] = useState<'tier' | 'cadence' | 'who' | null>(
    null,
  )
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  /* Whether the email field has been left, so what is wrong with it is said
     once somebody has finished typing rather than on the first keystroke. */
  const [emailLeft, setEmailLeft] = useState(false)
  const [said, setSaid] = useState(false)

  const [startsOn, setStartsOn] = useState(today)
  const [startOpen, setStartOpen] = useState(false)

  const [addressLine, setAddressLine] = useState('')
  const [city, setCity] = useState('')
  const [place, setPlace] = useState<Place>({
    country: DEFAULT_COUNTRY,
    region: '',
  })
  const [postalCode, setPostalCode] = useState('')
  const [homePhone, setHomePhone] = useState('')
  const [cellPhone, setCellPhone] = useState('')
  const [houseDone, setHouseDone] = useState(false)

  const [people, setPeople] = useState<Person[] | null>(null)
  const [peopleDone, setPeopleDone] = useState(false)

  const [token, setToken] = useState<string | null>(null)
  const [spentAt, setSpentAt] = useState(0)

  const checkout = useMutation({
    ...trpc.donation.membershipCheckout.mutationOptions(),
  })
  const confirmation = useCheckoutConfirmation({
    onComplete: onCheckoutComplete,
  })

  /* An open checkout was created from the answers as they stood, so a changed
     answer discards it and keeps every answer: the next Continue opens a
     checkout for what is then said, and the one left behind can never be
     confirmed. */
  const resetCheckout = checkout.reset
  const resetConfirmation = confirmation.reset
  const discardCheckout = useCallback(() => {
    resetCheckout()
    resetConfirmation()
  }, [resetCheckout, resetConfirmation])

  /* Read by a request that resolves after the page has hidden this branch,
     which sees the render it was sent from rather than the one it lands in. */
  const heldRef = useRef(held)
  heldRef.current = held
  useEffect(() => {
    if (held) discardCheckout()
  }, [held, discardCheckout])

  const tier = tiers.find((t) => t.id === tierId) ?? null
  /* One tier is not a question, the same as one branch: it folds in already
     answered rather than asking which of one is wanted. */
  const sole = tiers.length === 1 ? tiers[0] : null
  const chosen = tier ?? sole
  const tierWasChosen = chosen !== null && chosen !== sole

  const rateCents = chosen
    ? rateForCadence(chosen.monthlyCents, cadence ?? 'monthly')
    : 0
  const dueTodayCents = 0
  const consent = useQuery({
    ...trpc.donation.membershipConsent.queryOptions({
      shortId: orgId,
      tierId: chosen?.id ?? '00000000-0000-0000-0000-000000000000',
      cadence: cadence ?? 'monthly',
      startsOn,
    }),
    enabled: Boolean(chosen && cadence),
  })

  /* The head is the person paying, and everybody else is added by name. Built
     once, when the household question is answered, so what was typed on the way
     here is the starting point rather than a second set of fields. */
  const startPeople = (): Person[] => {
    const [first, ...rest] = name.trim().split(/\s+/)
    return [
      {
        id: crypto.randomUUID(),
        role: 'head',
        firstName: first ?? '',
        lastName: rest.join(' '),
        aliyaTyped: '',
        yichus: '',
      },
    ]
  }

  /* A reopened question is the one in hand until it is answered again, with
     every answer below it held rather than cleared. */
  const stage:
    | 'tier'
    | 'cadence'
    | 'who'
    | 'donor'
    | 'household'
    | 'people'
    | 'pay' =
    joining === null || reopened === 'who'
      ? 'who'
      : !joining
        ? 'donor'
        : !chosen || reopened === 'tier'
          ? 'tier'
          : cadence === null || reopened === 'cadence'
            ? 'cadence'
            : !said
              ? 'donor'
              : joining && !houseDone
                ? 'household'
                : joining && !peopleDone
                  ? 'people'
                  : 'pay'

  /* A person who typed a Hebrew name the server could not read, or one whose
     reading leaves the yichus open, is asked before anything is stored. */
  const [readings, setReadings] = useState<
    Record<string, { typed: string; reading: Reading }>
  >({})
  const noteReading = useCallback(
    (id: string, typed: string, reading: Reading) => {
      setReadings((was) => ({ ...was, [id]: { typed, reading } }))
    },
    [],
  )
  /* A reading answers the text it was read from and no other. Held to that,
     because the parse trails the typing: a line edited from one the server
     could resolve to one it cannot would otherwise be committed against the
     older answer, and the yichus never asked. */
  const readingFor = (p: Person): Reading | null => {
    const held = readings[p.id]
    return held && held.typed === p.aliyaTyped ? held.reading : null
  }
  const peopleReady = (people ?? []).every((p) => {
    if (p.firstName.trim().length === 0) return false
    if (p.aliyaTyped.trim().length === 0) return true
    const reading = readingFor(p)
    if (!reading) return false
    return !reading.questions.includes('yichus') || p.yichus !== ''
  })

  const ready =
    chosen !== null &&
    cadence !== null &&
    joining !== null &&
    name.trim().length > 0 &&
    isEmailAddress(email) &&
    (!joining || peopleReady) &&
    Boolean(consent.data?.consentText) &&
    (!challenge.data?.siteKey || token !== null)

  const submit = () => {
    if (!ready || !chosen || !cadence || joining === null) return
    const answers: MembershipAnswers = {
      tierId: chosen.id,
      tierLabel: chosen.label,
      cadence,
      startsOn,
      dueTodayCents,
      rateCents,
      joining,
      donorName: name.trim(),
      donorEmail: email.trim(),
    }
    /* The answers stored are the ones being paid for, and only those: a gift
       tried first and left would otherwise be what the thank-you names after a
       reload. */
    forgetAnswers(orgId)
    rememberMembership(orgId, answers)
    checkout.mutate(
      {
        shortId: orgId,
        tierId: chosen.id,
        cadence,
        startsOn,
        consentText: consent.data?.consentText ?? '',
        donorName: name.trim(),
        donorEmail: email.trim(),
        challenge: token,
        join: {
          addressLine: addressLine.trim() || undefined,
          city: city.trim() || undefined,
          region: place.region || undefined,
          country: place.country as CountryCode,
          postalCode: postalCode.trim() || undefined,
          homePhone: homePhone.trim() || undefined,
          people: (people ?? []).map((p) => ({
            firstName: p.firstName.trim(),
            lastName: p.lastName.trim() || undefined,
            role: p.role,
            gender: null,
            aliyaTyped: p.aliyaTyped.trim() || undefined,
            yichus: p.yichus === '' ? null : p.yichus,
            cellPhone:
              p.role === 'head' ? cellPhone.trim() || undefined : undefined,
          })),
        },
      },
      {
        /* The stored answers learn which checkout they were opened as, so a
           payment returning from a redirect is thanked from them only if they
           are the ones it paid for. */
        onSuccess: ({ checkoutId }) => {
          if (heldRef.current) {
            discardCheckout()
            return
          }
          rememberMembership(orgId, { ...answers, checkoutId })
        },
        onSettled: () => setSpentAt(Date.now()),
      },
    )
  }

  /* An answer given again keeps everything below it that still means the same
     thing. Another tier keeps the cadence, because every tier is offered
     monthly and yearly alike, priced from its own monthly rate — the answer
     stands and the figure beside it follows the tier. */
  const chooseTier = (id: string) => {
    discardCheckout()
    setTierId(id)
    setReopened(null)
  }

  /* Another cadence invalidates nothing below it: the start date is bounded
     by the shul's day rather than by the cadence, and what either charges is
     worked out from the answers as they then stand. */
  const chooseCadence = (next: 'monthly' | 'annual') => {
    discardCheckout()
    setCadence(next)
    setReopened(null)
  }

  /* The join answers remain held while the self-service-link path is shown, so
     changing back to joining finds them as they were typed. */
  const chooseJoining = (next: boolean) => {
    discardCheckout()
    setJoining(next)
    setReopened(null)
  }

  /* The question asked again opens on the answer it already has, so giving
     the same answer is one Continue away. */
  const reopen = (question: 'tier' | 'cadence' | 'who') => {
    discardCheckout()
    setStartOpen(false)
    if (question === 'tier') setPendingTier(chosen?.id ?? '')
    if (question === 'cadence') setPendingCadence(cadence ?? '')
    setReopened(question)
  }

  const tierOptions: ChoiceOption[] = tiers.map((t) => ({
    value: t.id,
    label: t.label,
    description: t.description ?? undefined,
    amount: money(t.monthlyCents),
    mark: '/month',
  }))

  const cadenceOptions: ChoiceOption[] = chosen
    ? [
        {
          value: 'monthly',
          label: 'Monthly',
          amount: money(chosen.monthlyCents),
          mark: '/month',
        },
        {
          value: 'annual',
          label: 'Yearly',
          description: 'Twelve months, paid together.',
          amount: money(annualCentsFor(chosen.monthlyCents)),
          mark: '/year',
        },
      ]
    : []

  /* The card step, beside the checkout it belongs to. It draws itself in the
     place of the answers, which stay mounted underneath it — so Back finds the
     household and every name exactly as they were typed. */
  let payment: ReactNode = null
  if (checkout.data && checkout.variables && !held) {
    const checkoutTier = tiers.find(
      (item) => item.id === checkout.variables.tierId,
    )
    const checkoutRate = checkoutTier
      ? rateForCadence(checkoutTier.monthlyCents, checkout.variables.cadence)
      : 0
    const monthly = checkout.variables.cadence === 'monthly'

    payment = (
      <PaymentStep
        orgId={orgId}
        card={checkout.data.card}
        what={`${checkoutTier?.label ?? 'Your'} membership`}
        who={`${checkout.variables.join ? 'Joining' : 'Renewing'} as ${checkout.variables.donorName}, ${checkout.variables.donorEmail}`}
        amount={money(checkoutRate)}
        amountNote={`${monthly ? 'a month ' : ''}from ${readCivilDate(checkout.variables.startsOn, 'long')}`}
        payLabel={'Save card'}
        fine={`Nothing is charged today. Your card is kept and first charged on ${readCivilDate(checkout.variables.startsOn, 'long')}.`}
        confirmation={confirmation}
        onBack={discardCheckout}
      />
    )
  }

  return (
    <>
      {/* Every question already answered, each with the way back to it. A
          reopened question hides its own line and every line below it until
          it is answered again. */}
      {joining && chosen && tierWasChosen && reopened !== 'tier' && (
        <AnswerLine
          appearance="page"
          label="Membership"
          value={chosen.label}
          detail={`${money(chosen.monthlyCents)} a month`}
          changeLabel="Change the membership"
          onChange={() => reopen('tier')}
        />
      )}
      {joining &&
        cadence !== null &&
        reopened !== 'tier' &&
        reopened !== 'cadence' && (
          <AnswerLine
            appearance="page"
            label="Paying"
            value={cadence === 'monthly' ? 'Monthly' : 'Yearly'}
            detail={money(rateCents)}
            changeLabel="Change how often you pay"
            onChange={() => reopen('cadence')}
          />
        )}
      {joining !== null && reopened === null && (
        <AnswerLine
          appearance="page"
          label="You are"
          value={joining ? 'Join' : 'Renew or update my membership'}
          changeLabel="Change whether you are joining or renewing"
          onChange={() => reopen('who')}
        />
      )}
      {/* A question with a sensible answer is not asked: it folds in already
          answered, and the case it exists for is somebody who has already given
          this month. */}
      {joining && cadence !== null && !startOpen && reopened === null && (
        <AnswerLine
          appearance="page"
          label="Starts"
          value={startsOn === today ? 'Today' : readCivilDate(startsOn, 'long')}
          changeLabel="Change when the membership starts"
          onChange={() => {
            discardCheckout()
            setStartOpen(true)
          }}
        />
      )}

      {startOpen && (
        <Question
          ref={stepRef}
          ask="When should the membership start?"
          appearance="page"
        >
          <Field label="Starts on">
            <DateInput
              value={startsOn}
              min={today}
              max={furthestStart(today)}
              onChange={(e) => setStartsOn(e.target.value)}
            />
          </Field>
          <p className="donate__fine">
            Nothing is due today. Your card is kept and first charged on{' '}
            {readCivilDate(startsOn, 'long')}.
          </p>
          <Button variant="secondary" onClick={() => setStartOpen(false)}>
            Continue
          </Button>
        </Question>
      )}

      {!startOpen && stage === 'tier' && (
        <Question ref={stepRef} ask="Which membership?" appearance="page">
          <Choice
            ariaLabel="Which membership?"
            options={tierOptions}
            value={pendingTier}
            onValueChange={setPendingTier}
          />
          <Button
            variant="secondary"
            disabled={pendingTier === ''}
            onClick={() => chooseTier(pendingTier)}
          >
            Continue
          </Button>
        </Question>
      )}

      {!startOpen && stage === 'cadence' && (
        <Question
          ref={stepRef}
          ask="How would you like to pay?"
          appearance="page"
        >
          <Choice
            ariaLabel="How would you like to pay?"
            options={cadenceOptions}
            value={pendingCadence}
            onValueChange={setPendingCadence}
          />
          <Button
            variant="secondary"
            disabled={pendingCadence === ''}
            onClick={() =>
              chooseCadence(pendingCadence === 'annual' ? 'annual' : 'monthly')
            }
          >
            Continue
          </Button>
        </Question>
      )}

      {!startOpen && stage === 'who' && (
        <Question
          ref={stepRef}
          ask="Would you like to join or update a membership?"
          appearance="page"
        >
          <BranchList appearance="page">
            <BranchRow
              label="Join"
              note="New to the shul — we will ask for your household's details."
              onSelect={() => chooseJoining(true)}
            />
            <BranchRow
              label="Renew or update my membership"
              note="Already a member — we will email you a private link to your account."
              onSelect={() => chooseJoining(false)}
            />
          </BranchList>
        </Question>
      )}

      {!startOpen && stage === 'donor' && !joining && (
        <AccountLinkRequest orgId={orgId} stepRef={stepRef} />
      )}

      {!startOpen && stage === 'donor' && joining && (
        <Question
          ref={stepRef}
          ask="Who is this membership for?"
          appearance="page"
        >
          <Field label="Your name">
            <TextInput
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field
            label="Email"
            problem={
              emailLeft && email.trim() && !isEmailAddress(email)
                ? EMAIL_PROBLEM
                : null
            }
          >
            <TextInput
              type="email"
              onBlur={() => setEmailLeft(true)}
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Button
            variant="secondary"
            disabled={name.trim().length === 0 || email.trim().length === 0}
            onClick={() => setSaid(true)}
          >
            Continue
          </Button>
        </Question>
      )}

      {!startOpen && stage === 'household' && (
        <Question
          ref={stepRef}
          ask="Where does the household live?"
          appearance="page"
        >
          <Field label="Address" optional>
            <TextInput
              autoComplete="street-address"
              value={addressLine}
              onChange={(e) => setAddressLine(e.target.value)}
            />
          </Field>
          <FieldRow cols={2}>
            <Field label="City" optional>
              <TextInput
                autoComplete="address-level2"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </Field>
            <Field label={postalLabel(place.country)} optional>
              <TextInput
                autoComplete="postal-code"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
              />
            </Field>
          </FieldRow>
          <AddressPlace value={place} onValueChange={setPlace} regionOptional />
          <FieldRow cols={2}>
            <Field label="Home phone" optional>
              <TextInput
                type="tel"
                value={homePhone}
                onChange={(e) => setHomePhone(e.target.value)}
              />
            </Field>
            <Field label="Cell phone" optional>
              <TextInput
                type="tel"
                autoComplete="tel"
                value={cellPhone}
                onChange={(e) => setCellPhone(e.target.value)}
              />
            </Field>
          </FieldRow>
          <Button
            variant="secondary"
            onClick={() => {
              if (people === null) setPeople(startPeople())
              setHouseDone(true)
            }}
          >
            Continue
          </Button>
        </Question>
      )}

      {!startOpen && stage === 'people' && (
        <Question ref={stepRef} ask="Who is called up?" appearance="page">
          <Ask>
            The names the gabbai calls up. Type each one as it is said — we read
            it back so you can check it before it is written down.
          </Ask>
          {(people ?? []).map((p, i) => (
            <PersonRow
              key={p.id}
              person={p}
              title={
                p.role === 'head'
                  ? 'Head of household'
                  : p.firstName.trim() || `Person ${i + 1}`
              }
              onChange={(next) =>
                setPeople((rows) =>
                  (rows ?? []).map((row) => (row.id === p.id ? next : row)),
                )
              }
              onRemove={
                p.role === 'head'
                  ? undefined
                  : () =>
                      setPeople((rows) =>
                        (rows ?? []).filter((row) => row.id !== p.id),
                      )
              }
              onReading={noteReading}
            />
          ))}
          <Button
            variant="quiet"
            onClick={() =>
              setPeople((rows) => [
                ...(rows ?? []),
                {
                  id: crypto.randomUUID(),
                  role: 'child',
                  firstName: '',
                  lastName: '',
                  aliyaTyped: '',
                  yichus: '',
                },
              ])
            }
          >
            Add another person
          </Button>
          <Button
            variant="secondary"
            disabled={!peopleReady}
            onClick={() => setPeopleDone(true)}
          >
            Continue
          </Button>
        </Question>
      )}

      {!startOpen && stage === 'pay' && chosen && cadence && (
        <Question ref={stepRef} ask="Confirm the membership" appearance="page">
          <div className="donate__total">
            <TotalLine
              label={`${chosen.label} membership`}
              amount={money(rateCents)}
              note={cadence === 'monthly' ? 'each month' : 'for the year'}
            />
            <TotalLine
              label="Due today"
              amount={money(dueTodayCents)}
              emphasis="total"
            />
            <TotalLine
              label="First payment"
              amount={money(rateCents)}
              note={`on ${readCivilDate(startsOn, 'long')}`}
            />
          </div>

          {consent.data?.consentText && <Ask>{consent.data.consentText}</Ask>}

          <Challenge
            siteKey={challenge.data?.siteKey ?? null}
            onToken={setToken}
            spentAt={spentAt}
          />

          {checkout.error && (
            <Notice tone="error">{checkout.error.message}</Notice>
          )}
          <Button onClick={submit} disabled={!ready || checkout.isPending}>
            {continueLabel(checkout.isPending)}
          </Button>
          <p className="donate__fine">
            Next, your card. Nothing is charged today; it is kept and first
            charged on {readCivilDate(startsOn, 'long')}.
          </p>
        </Question>
      )}
      {payment}
    </>
  )
}

/**
 * One person, and the shem la'aliya read back as the server understood it.
 *
 * **The reading is shown to the one person who can correct it.** A wrong split
 * — the name divided at the wrong word, a title taken for a name — is a wrong
 * name called out in shul, and nothing downstream can catch it.
 */
function PersonRow({
  person,
  title,
  onChange,
  onRemove,
  onReading,
}: {
  person: Person
  /**
   * What names this block.
   *
   * Every person is asked the same three questions, so a second block repeats
   * every label in the first — which leaves somebody reading the form by its
   * labels alone with no way to tell whose name they are filling in.
   */
  title: string
  onChange: (next: Person) => void
  onRemove?: () => void
  /** Stable, so the effect reporting a reading can name it as a dependency. */
  onReading: (id: string, typed: string, reading: Reading) => void
}) {
  const trpc = useTRPC()
  const parse = useMutation(trpc.member.parseAliyaName.mutationOptions())
  const typed = person.aliyaTyped
  const id = person.id
  const { mutate, data, variables } = parse

  /* Read after the typing settles: a parse per keystroke would show a reading
     of half a name, which is the one thing this row exists to prevent. */
  useEffect(() => {
    if (typed.trim().length === 0) return
    const at = setTimeout(() => mutate({ typed }), 400)
    return () => clearTimeout(at)
  }, [typed, mutate])

  /* The parent decides whether the row may be committed, so what the server
     read has to reach it. A row whose name has been cleared is answered by the
     empty field rather than by a stale reading, which is why nothing is
     reported back on the way to empty. */
  useEffect(() => {
    if (data !== undefined && variables) onReading(id, variables.typed, data)
  }, [data, variables, id, onReading])

  /* What is on screen is a reading of what is in the box, or nothing. A
     reading of the name as it was two keystrokes ago is the wrong name shown
     for checking, which is the one thing this row exists to prevent. */
  const answered = data !== undefined && variables?.typed === typed
  const reading = answered ? data : null
  const asks = reading?.questions.includes('yichus') ?? false

  return (
    <FieldSection title={title}>
      <FieldRow cols={2}>
        <Field label="First name">
          <TextInput
            value={person.firstName}
            onChange={(e) => onChange({ ...person, firstName: e.target.value })}
          />
        </Field>
        <Field label="Last name" optional>
          <TextInput
            value={person.lastName}
            onChange={(e) => onChange({ ...person, lastName: e.target.value })}
          />
        </Field>
      </FieldRow>
      <Field
        label="Name for an aliya"
        optional
        info={
          <>
            As the gabbai says it — his name, <span dir="rtl">בן</span>, and his
            father's name, with <span dir="rtl">הכהן</span> or{' '}
            <span dir="rtl">הלוי</span> where it applies.
          </>
        }
      >
        <TextInput
          className="control--rtl"
          dir="rtl"
          lang="he"
          value={typed}
          maxLength={200}
          onChange={(e) => onChange({ ...person, aliyaTyped: e.target.value })}
        />
      </Field>
      {/* A line the server could not read is answered too, and says so: the row
          is held back until it reads or the field is cleared, so a screen
          showing nothing would be a Continue button disabled for no stated
          reason. */}
      {typed.trim().length > 0 && answered && (
        <AliyaNameReading reading={reading} />
      )}
      {typed.trim().length > 0 && parse.isError && (
        <Notice tone="note">
          We could not read that back just now. Check the line, or clear it and
          we will take the name later.
        </Notice>
      )}
      {asks && (
        <Field
          label="Kohen, levi or yisrael"
          info={
            <>
              <span dir="rtl">ישראל</span> carries no mark at the end of the
              name, so the line cannot say which. This is the only place it is
              asked.
            </>
          }
        >
          <Select
            options={YICHUS}
            value={person.yichus === '' ? null : person.yichus}
            placeholder="Choose one"
            onValueChange={(v) => onChange({ ...person, yichus: v })}
          />
        </Field>
      )}
      {onRemove && (
        <Button variant="quiet" onClick={onRemove}>
          Remove this person
        </Button>
      )}
    </FieldSection>
  )
}
